import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { postsApi, timelineApi, usersApi } from '@/api/endpoints'
import { keys } from '@/api/keys'
import type { Page, Post, TimelineKind, UserTab } from '@/api/types'

const pageOptions = {
  initialPageParam: undefined as string | undefined,
  getNextPageParam: (last: Page<Post>) => last.next_cursor ?? undefined,
}

export function useHomeTimeline(enabled = true, kind: TimelineKind = 'all') {
  return useInfiniteQuery({
    queryKey: keys.timelineHome(kind),
    queryFn: ({ pageParam }) => timelineApi.home(pageParam, 20, kind),
    enabled,
    ...pageOptions,
  })
}

export function useExploreTimeline(kind: TimelineKind = 'all') {
  return useInfiniteQuery({
    queryKey: keys.timelineExplore(kind),
    queryFn: ({ pageParam }) => timelineApi.explore(pageParam, 20, kind),
    ...pageOptions,
  })
}

/**
 * 为你推荐。翻页读的是服务端的排序快照（D84）；快照过期重排时可能重复几条，这里按 id 去重兜底。
 *
 * 不在切回窗口时自动重取：重取就是重新排序，正在读的列表会在眼前整个换掉。
 */
export function useForYouTimeline(enabled = true, kind: TimelineKind = 'all') {
  return useInfiniteQuery({
    queryKey: keys.timelineForYou(kind),
    queryFn: ({ pageParam }) => timelineApi.forYou(pageParam, 20, kind),
    enabled,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    select: (data) => {
      const seen = new Set<string>()
      return {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          items: page.items.filter((post) => {
            if (seen.has(post.id)) return false
            seen.add(post.id)
            return true
          }),
        })),
      }
    },
    ...pageOptions,
  })
}

export function useUserTimeline(username: string, tab: UserTab) {
  return useInfiniteQuery({
    queryKey: keys.timelineUser(username, tab),
    queryFn: ({ pageParam }) => usersApi.posts(username, tab, pageParam),
    enabled: Boolean(username),
    ...pageOptions,
  })
}

export function usePostReplies(postId: string) {
  return useInfiniteQuery({
    queryKey: keys.postReplies(postId),
    queryFn: ({ pageParam }) => postsApi.replies(postId, pageParam),
    enabled: Boolean(postId),
    ...pageOptions,
  })
}

/** 顶部"有 N 条新帖"：每 60 秒查一次数量，不拉内容。 */
export function useNewPostCount(firstPostId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['timeline', 'home', 'new-count', firstPostId],
    queryFn: () => timelineApi.homeNewCount(firstPostId!),
    enabled: enabled && Boolean(firstPostId),
    refetchInterval: 60_000,
    staleTime: 30_000,
  })
}
